import React from 'react';

import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { useAllInsights } from '@/components/pages/Insights/useInsights';
import useDialogs from '@/hooks/useDialogs';
import useEntities from '@/hooks/useEntities';
import useMembers from '@/hooks/useMembers';
import { useRoles } from '@/hooks/useRoles';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Member } from '@/shared/types/member';
import { Role } from '@/shared/types/role';
import { DEFAULT_COLLECTION } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';
import { AuditDiffEntry, AuditFieldChange, parseAuditLogDiff, UNSET_VALUE } from '@/util/diffUtils';

type UngroupedLogProps = {
  avatar: React.JSX.Element;
  subject: string;
  verb: string;
  targetType: string;
  targetName: string | null;
  targetId: string;
  brainId: string | null;
  brain: string | undefined;
  deskId: string | null;
  desk: string | undefined;
  timestamp: string | [string, string];
  extra: Record<string, unknown>;
};

type GroupedLogProps = { handleToggle: () => void; isGrouped: boolean; groupedItems: number };

type ConnectorProps = {
  /** Show a vertical connector line below this entry */
  showConnector?: boolean;
};

type ContextProps = {
  /** Hide the AI agent hyperlink (useful when already on that agent's page) */
  hideAgentLink?: boolean;
  /** Hide the trailing timestamp (the new feed renders it in a left gutter instead) */
  hideTimestamp?: boolean;
  /** Resolve an AI-agent id to its name for diff bullets (the feed's shared lookup) */
  getBrain?: (id: string | null) => string | undefined;
};

type LayoutProps = {
  /** Left indent in Tailwind spacing (e.g. 'pl-5') for nested/grouped items */
  indentClass?: string;
  /** Content rendered inside the row, below the sentence (e.g. nested sub-activities) */
  children?: React.ReactNode;
};

type Props = UngroupedLogProps &
  Partial<GroupedLogProps> &
  ConnectorProps &
  ContextProps &
  LayoutProps;

const LINK_CLASSES = 'text-primary hover:underline';

const formatNames = (names: string[]) => {
  return names.join(', ');
};

const getNames = (arr, extra: UngroupedLogProps['extra']) => {
  if (!arr || !extra) {
    return;
  }

  const id = extra?.role_id || extra?.role_ids ? 'role_id' : 'user_id';
  const ids = (extra?.role_ids || extra?.user_ids ? extra[`${id}s`] : extra[id]) as string[];

  const names = arr.reduce((acc: string[], current: Member | Role) => {
    if (ids.includes(current[id])) {
      acc.push(current.name);
    }

    return acc;
  }, []);

  return formatNames(names);
};

// Returns null when a target has no in-app page, so callers can render it
// as plain text instead of a dead link.
const getTargetLink = (
  targetType: UngroupedLogProps['targetType'],
  targetId: UngroupedLogProps['targetId'],
  slug: string,
  brainId: UngroupedLogProps['brainId'],
  deskId: UngroupedLogProps['deskId']
): string | null => {
  switch (targetType) {
    case 'dialogs':
    case 'intents':
    case 'entities':
    case 'webhooks':
      return `/${slug}/ai-agents/${brainId}/workflows/${targetType}/${targetId}`;
    case 'insights':
      return `/${slug}/ai-agents/${brainId}/insights`;
    case 'mcp_servers':
      return `/${slug}/ai-agents/${brainId}/mcps`;
    case 'simulations':
      return `/${slug}/ai-agents/${brainId}/simulations`;
    case 'rules':
    case 'business_hours':
    case 'integrations':
      return `/${slug}/environments/${deskId}/${targetType}/${targetId}`;
    case 'desks':
      return `/${slug}/environments/${targetId}`;
    case 'brains':
      return `/${slug}/ai-agents/${targetId}`;
    case 'teams':
      return `/${slug}/account/manage/team-access/`;
    case 'conversations':
      return `/${slug}/chats/${deskId}/conversations/${targetId}`;
    case 'bundles':
      return `/${slug}/environments/${deskId}/context_bundles/${targetId}`;
  }

  return null;
};

/** Turn a raw backend token into a readable label (e.g. "model_strategy" → "model strategy") */
const humanize = (value: string) => value.replace(/_/g, ' ');

/**
 * Resolve a backend token to its translation via a static literal-key map,
 * humanizing `fallbackText` when the token has no mapped key (never leak a raw key).
 */
export const translateToken = (
  map: Record<string, string>,
  token: string,
  t: TFunction,
  fallbackText = token
) => {
  const key = map[token];
  return key ? t(key) : humanize(fallbackText);
};

// Static map: backend verb → literal i18n key. Keys are full literals (not
// interpolated) so lint/TS and /i18n-review can detect missing translations.
// The backend verb space is open and spans multiple services, so unknown verbs
// fall back to a humanized label — a raw key must never reach the UI.
const VERB_KEYS: Record<string, string> = {
  added_permission: 'activityLogs.verbs.added_permission',
  added_promotion_code: 'activityLogs.verbs.added_promotion_code',
  added_roles: 'activityLogs.verbs.added_roles',
  added_users: 'activityLogs.verbs.added_users',
  attached_payment_method: 'activityLogs.verbs.attached_payment_method',
  configured_auto_ai: 'activityLogs.verbs.configured_auto_ai',
  created: 'activityLogs.verbs.created',
  created_subscription: 'activityLogs.verbs.created_subscription',
  created_version: 'activityLogs.verbs.created_version',
  deleted: 'activityLogs.verbs.deleted',
  detached_payment_method: 'activityLogs.verbs.detached_payment_method',
  disabled_auto_ai: 'activityLogs.verbs.disabled_auto_ai',
  member_left: 'activityLogs.verbs.member_left',
  posted_auto_ai_results: 'activityLogs.verbs.posted_auto_ai_results',
  published_version: 'activityLogs.verbs.published_version',
  ran: 'activityLogs.verbs.ran',
  removed: 'activityLogs.verbs.removed',
  removed_permission: 'activityLogs.verbs.removed_permission',
  removed_role: 'activityLogs.verbs.removed_role',
  removed_subscription: 'activityLogs.verbs.removed_subscription',
  removed_user: 'activityLogs.verbs.removed_user',
  removed_version: 'activityLogs.verbs.removed_version',
  reordered_rules: 'activityLogs.verbs.reordered_rules',
  responded: 'activityLogs.verbs.responded',
  trained: 'activityLogs.verbs.trained',
  training_requested: 'activityLogs.verbs.training_requested',
  updated: 'activityLogs.verbs.updated',
  updated_collection: 'activityLogs.verbs.updated_collection',
  updated_customer: 'activityLogs.verbs.updated_customer',
  updated_guidelines: 'activityLogs.verbs.updated_guidelines',
  updated_permission: 'activityLogs.verbs.updated_permission',
  updated_tax_id: 'activityLogs.verbs.updated_tax_id',
};

// Static map: backend resource (target type) → literal singular-label i18n key.
const RESOURCE_KEYS: Record<string, string> = {
  accounts: 'activityLogs.resources_singular.accounts',
  ai_agents: 'activityLogs.resources_singular.ai_agents',
  broadcasts: 'activityLogs.resources_singular.broadcasts',
  bundles: 'activityLogs.resources_singular.bundles',
  business_hours: 'activityLogs.resources_singular.business_hours',
  campaign_senders: 'activityLogs.resources_singular.campaign_senders',
  campaigns: 'activityLogs.resources_singular.campaigns',
  collections: 'activityLogs.resources_singular.collections',
  companies: 'activityLogs.resources_singular.companies',
  contacts: 'activityLogs.resources_singular.contacts',
  conversations: 'activityLogs.resources_singular.conversations',
  credentials: 'activityLogs.resources_singular.credentials',
  datasources: 'activityLogs.resources_singular.datasources',
  departments: 'activityLogs.resources_singular.departments',
  desk_webhooks: 'activityLogs.resources_singular.desk_webhooks',
  desks: 'activityLogs.resources_singular.desks',
  dialogs: 'activityLogs.resources_singular.dialogs',
  documents: 'activityLogs.resources_singular.documents',
  entities: 'activityLogs.resources_singular.entities',
  filters: 'activityLogs.resources_singular.filters',
  insights: 'activityLogs.resources_singular.insights',
  integrations: 'activityLogs.resources_singular.integrations',
  intents: 'activityLogs.resources_singular.intents',
  invitations: 'activityLogs.resources_singular.invitations',
  language_models: 'activityLogs.resources_singular.language_models',
  macros: 'activityLogs.resources_singular.macros',
  mcp_servers: 'activityLogs.resources_singular.mcp_servers',
  members: 'activityLogs.resources_singular.members',
  model_strategy: 'activityLogs.resources_singular.model_strategy',
  plugins: 'activityLogs.resources_singular.plugins',
  recommendations: 'activityLogs.resources_singular.recommendations',
  reports: 'activityLogs.resources_singular.reports',
  roles: 'activityLogs.resources_singular.roles',
  rules: 'activityLogs.resources_singular.rules',
  simulations: 'activityLogs.resources_singular.simulations',
  slas: 'activityLogs.resources_singular.slas',
  teams: 'activityLogs.resources_singular.teams',
  tools: 'activityLogs.resources_singular.tools',
  users: 'activityLogs.resources_singular.users',
  views: 'activityLogs.resources_singular.views',
  webhooks: 'activityLogs.resources_singular.webhooks',
};

// Singular, translated label for a backend target type. Returns null for
// 'billing' (no label), reuses the ai_agents label for 'brains', and humanizes
// any target type the map doesn't cover.
const getSingularTargetType = (targetType: string, t: TFunction) => {
  if (targetType === 'billing') {
    return null;
  }

  const lookup = targetType === 'brains' ? 'ai_agents' : targetType;
  return translateToken(RESOURCE_KEYS, lookup, t, targetType);
};

// Literal-key selection for team/member prepositions (no dynamic key construction)
const teamPrepositionKey = (verb: string) =>
  verb.includes('removed') ? 'activityLogs.from_team' : 'activityLogs.to_team';

const memberPrepositionKey = (verb: string) =>
  verb.includes('removed') ? 'activityLogs.from_a_member' : 'activityLogs.to_a_member';

/** Translate a raw API verb (e.g. "created_version") via a static key map, humanizing unknowns */
const translateVerb = (verb: string, t: TFunction) =>
  translateToken(VERB_KEYS, verb.replace(/ /g, '_'), t, verb);

// Static map: backend field name → literal i18n key. Field paths come from
// arbitrary changed columns and are open-ended, so unknown parts humanize.
const FIELD_KEYS: Record<string, string> = {
  actions: 'activityLogs.fields.actions',
  active: 'activityLogs.fields.active',
  apikey: 'activityLogs.fields.apikey',
  brain_id: 'activityLogs.fields.brain_id',
  brain_parent_id: 'activityLogs.fields.brain_parent_id',
  brain_type: 'activityLogs.fields.brain_type',
  brain_version: 'activityLogs.fields.brain_version',
  business_hours_id: 'activityLogs.fields.business_hours_id',
  categories: 'activityLogs.fields.categories',
  collection: 'activityLogs.fields.collection',
  collection_id: 'activityLogs.fields.collection_id',
  condition: 'activityLogs.fields.condition',
  confidence_threshold: 'activityLogs.fields.confidence_threshold',
  config: 'activityLogs.fields.config',
  description: 'activityLogs.fields.description',
  enabled: 'activityLogs.fields.enabled',
  expressions: 'activityLogs.fields.expressions',
  fail_on_error: 'activityLogs.fields.fail_on_error',
  filter: 'activityLogs.fields.filter',
  first_reply_time_in_seconds: 'activityLogs.fields.first_reply_time_in_seconds',
  fuzzy_match: 'activityLogs.fields.fuzzy_match',
  greeklish: 'activityLogs.fields.greeklish',
  guidelines: 'activityLogs.fields.guidelines',
  headers: 'activityLogs.fields.headers',
  icon: 'activityLogs.fields.icon',
  inactivity_timeout: 'activityLogs.fields.inactivity_timeout',
  intervals: 'activityLogs.fields.intervals',
  is_active: 'activityLogs.fields.is_active',
  is_draft: 'activityLogs.fields.is_draft',
  is_hidden: 'activityLogs.fields.is_hidden',
  is_service_desk: 'activityLogs.fields.is_service_desk',
  keep_alive: 'activityLogs.fields.keep_alive',
  language: 'activityLogs.fields.language',
  language_model_id: 'activityLogs.fields.language_model_id',
  max_sessions_per_agent: 'activityLogs.fields.max_sessions_per_agent',
  metadata: 'activityLogs.fields.metadata',
  model: 'activityLogs.fields.model',
  name: 'activityLogs.fields.name',
  next_reply_time_in_seconds: 'activityLogs.fields.next_reply_time_in_seconds',
  nodes: 'activityLogs.fields.nodes',
  provider: 'activityLogs.fields.provider',
  routing: 'activityLogs.fields.routing',
  schedule: 'activityLogs.fields.schedule',
  status: 'activityLogs.fields.status',
  subscribers_context: 'activityLogs.fields.subscribers_context',
  text: 'activityLogs.fields.text',
  timezone: 'activityLogs.fields.timezone',
  token: 'activityLogs.fields.token',
  tools: 'activityLogs.fields.tools',
  triggers: 'activityLogs.fields.triggers',
  type: 'activityLogs.fields.type',
  url: 'activityLogs.fields.url',
  values: 'activityLogs.fields.values',
};

/** Get translated label for an API field path (e.g. "subscribers_context.tags" → "Subscribers: tags") */
const getFieldLabel = (field: string, t: TFunction): string => {
  const parts = field.split('.');
  const labels = parts.map((part) => translateToken(FIELD_KEYS, part, t));

  if (parts.length > 1) {
    return `${labels[0]}: ${labels.slice(1).join(' ')}`;
  }
  return labels[0];
};

/** Shared Trans components for bullet items (field_changed, field_changed_from_to) */
const bulletComponents: Record<string, React.JSX.Element> = {
  fieldLabel: <span className="font-medium text-muted-foreground" />,
  value: <span className="font-medium text-foreground" />,
};

// Field name (last path segment) → friendly on/off copy; boolean fields without an entry use the generic fallback.
const BOOLEAN_FIELD_KEYS: Record<string, { on: string; off: string }> = {
  active: { on: 'activityLogs.bool.active_on', off: 'activityLogs.bool.active_off' },
  enabled: { on: 'activityLogs.bool.enabled_on', off: 'activityLogs.bool.enabled_off' },
  fuzzy_match: { on: 'activityLogs.bool.fuzzy_match_on', off: 'activityLogs.bool.fuzzy_match_off' },
  improvements_enabled: {
    on: 'activityLogs.bool.improvements_on',
    off: 'activityLogs.bool.improvements_off',
  },
  insights_enabled: { on: 'activityLogs.bool.insights_on', off: 'activityLogs.bool.insights_off' },
  is_active: { on: 'activityLogs.bool.active_on', off: 'activityLogs.bool.active_off' },
  is_draft: { on: 'activityLogs.bool.draft_on', off: 'activityLogs.bool.draft_off' },
  is_hidden: { on: 'activityLogs.bool.hidden_on', off: 'activityLogs.bool.hidden_off' },
};

// Fields known to be boolean; membership (not the value) decides toggle copy.
const BOOLEAN_FIELDS = new Set<string>([
  ...Object.keys(BOOLEAN_FIELD_KEYS),
  'fail_on_error',
  'greeklish',
  'is_service_desk',
]);

// Friendly copy for a known boolean field's toggle, or null when the field isn't boolean or the change isn't a clean true↔false toggle (e.g. cleared to —).
const getBooleanChangeCopy = (change: AuditFieldChange, t: TFunction): string | null => {
  const key = change.field.split('.').pop()!;
  if (!BOOLEAN_FIELDS.has(key)) {
    return null;
  }
  const enabled = change.to === 'true';
  const disabled = change.to === 'false';
  if (!enabled && !disabled) {
    return null;
  }
  const override = BOOLEAN_FIELD_KEYS[key];
  if (override) {
    return t(enabled ? override.on : override.off);
  }
  return t(enabled ? 'activityLogs.bool.generic_on' : 'activityLogs.bool.generic_off', {
    0: getFieldLabel(change.field, t),
  });
};

const EMPTY_VALUE_TOKENS = new Set([UNSET_VALUE, '']);
const displayDiffValue = (value: string, t: TFunction): string =>
  EMPTY_VALUE_TOKENS.has(value) ? t('activityLogs.none') : value;

// Fields whose value is an agent id — show the name, never the UUID.
const AGENT_ID_FIELDS = new Set(['brain_id', 'brain_parent_id']);
export const resolveDiffValue = (
  field: string,
  value: string,
  getBrain?: (id: string) => string | undefined
): string => {
  const key = field.split('.').pop();
  if (key && AGENT_ID_FIELDS.has(key) && getBrain) {
    return getBrain(value) ?? value;
  }
  return value;
};

// Bullet content for one diff entry: name-only mention, friendly boolean copy, or from→to.
const renderDiffEntry = (
  entry: AuditDiffEntry,
  t: TFunction,
  resolveValue: (field: string, value: string) => string
): React.ReactNode => {
  if (entry.kind === 'name') {
    return (
      <Trans
        i18nKey="activityLogs.field_changed"
        values={{ 0: getFieldLabel(entry.field, t) }}
        components={bulletComponents}
      />
    );
  }

  const booleanCopy = getBooleanChangeCopy(entry, t);
  if (booleanCopy) {
    return booleanCopy;
  }

  return (
    <Trans
      i18nKey="activityLogs.field_changed_from_to"
      values={{
        0: getFieldLabel(entry.field, t),
        1: displayDiffValue(resolveValue(entry.field, entry.from), t),
        2: displayDiffValue(resolveValue(entry.field, entry.to), t),
      }}
      components={bulletComponents}
    />
  );
};

/** Bullet list of a log's field changes (any API diff format), in payload order. */
export const AuditLogFieldChanges = ({
  extra,
  className,
  getBrain,
}: {
  extra: Record<string, unknown>;
  className?: string;
  getBrain?: (id: string) => string | undefined;
}) => {
  const { t } = useTranslation();
  const resolveValue = (field: string, value: string) => resolveDiffValue(field, value, getBrain);
  const entries = parseAuditLogDiff(extra);

  if (entries.length === 0) {
    return null;
  }

  return (
    <ul
      className={cn(
        'list-disc space-y-0.5 pl-4 text-sm leading-5 break-words text-muted-foreground marker:text-muted-foreground/60',
        className
      )}
    >
      {entries.map((entry, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={`${entry.kind}-${entry.field}-${index}`}>
          {renderDiffEntry(entry, t, resolveValue)}
        </li>
      ))}
    </ul>
  );
};

type SentenceTransProps = {
  i18nKey: string;
  values: Record<string, string>;
  components: Record<string, React.JSX.Element>;
};

const TileActivityLog = ({
  avatar,
  subject,
  verb,
  targetType,
  targetName,
  targetId,
  brainId,
  brain,
  deskId,
  desk,
  timestamp,
  extra,
  handleToggle = () => {},
  isGrouped = false,
  groupedItems = 0,
  showConnector = false,
  hideAgentLink = false,
  hideTimestamp = false,
  getBrain,
  indentClass,
  children,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const { roles } = useRoles();
  const slug = useSelector(selectAccountSlug);
  const { members } = useMembers();
  const { entity } = useEntities(brainId, targetId);
  const { data: allInsights, status: insightsStatus } = useAllInsights(brainId);

  const disabledEntity = entity?.entity !== targetName && targetType === 'entities';
  const deletedDesk = desk === undefined && targetType === 'desks';
  const deletedBrain = brain === undefined && targetType === 'brains';
  // Insight is deleted if it's not found in the list of all insights
  const deletedInsight =
    targetType === 'insights' &&
    insightsStatus === 'success' &&
    !allInsights?.some((i) => i.insight_id === targetId);

  const { dialog } = useDialogs(
    brainId,
    targetType === 'dialogs' && verb !== 'removed' ? targetId : undefined
  );

  // ── Build full-sentence Trans props ──────────────────────────────────
  // Each rendering path produces ONE Trans that owns the complete sentence
  // (subject + verb + all contextual parts), so translators can reorder
  // every part according to the target language's grammar.
  const getSentence = (): SentenceTransProps => {
    const bold = <span className="font-medium" />;
    const muted = <span className="text-muted-foreground" />;

    // 1. User management (added/removed users to/from team)
    if (extra && (extra.user_id || extra.user_ids)) {
      const teamLink =
        targetType === 'teams' ? (
          <Link
            className={LINK_CLASSES}
            to={getTargetLink(targetType, targetId, slug, brainId, deskId)}
          />
        ) : (
          muted
        );

      return {
        i18nKey: 'activityLogs.sentence_user_mgmt',
        values: {
          0: subject,
          1: translateVerb(verb, t),
          2: getNames(members, extra) || '',
          3: t(teamPrepositionKey(verb)),
          4: targetName ?? targetId,
        },
        components: { bold, memberName: muted, target: teamLink },
      };
    }

    // 2. Role management (assigned/removed roles)
    if (extra && (extra.role_id || extra.role_ids)) {
      return {
        i18nKey: 'activityLogs.sentence_role_mgmt',
        values: {
          0: subject,
          1: translateVerb(verb, t),
          2: getNames(roles, extra) || '',
          // TODO: Uncomment when #DEV-2031 is Done
          // 3: t(`activityLogs.${preposition(verb)}_member`, { 0: targetName }),
          3: t(memberPrepositionKey(verb)),
          4: getSingularTargetType(targetType, t) || '',
          5: String(targetName ?? ''),
        },
        components: { bold, roleName: muted, memberName: muted },
      };
    }

    // 3. Invitation
    if (targetType === 'invitations') {
      return {
        i18nKey: 'activityLogs.sentence_invitation',
        values: {
          0: subject,
          1: translateVerb(verb, t),
          2: getSingularTargetType(targetType, t) || '',
          3: targetId,
        },
        components: { bold, muted },
      };
    }

    // ── Shared values for remaining templates ──────────────────────────
    const resourceType =
      !extra?.user_ids && !extra?.version && verb !== 'removed role' && verb !== 'removed user'
        ? getSingularTargetType(targetType, t) || ''
        : '';

    const targetLink = getTargetLink(targetType, targetId, slug, brainId, deskId);

    const showTargetLink =
      !extra?.version &&
      targetType !== 'users' &&
      verb !== 'removed' &&
      targetType !== 'invitations' &&
      !deletedBrain &&
      !deletedDesk &&
      !deletedInsight &&
      !disabledEntity &&
      !(hideAgentLink && targetType === 'brains') &&
      (desk !== undefined || brain !== undefined || targetType === 'teams');

    // Brain context
    const showBrainContext =
      !!brainId &&
      !hideAgentLink &&
      (targetType !== 'brains' || verb === 'created version' || !!extra?.version);

    const brainContextText = showBrainContext
      ? extra?.version
        ? t('activityLogs.of', { 0: t('common.ai_agent') })
        : brain
          ? t('activityLogs.in', { 0: t('common.ai_agent') })
          : t('activityLogs.in_a_deleted_ai_agent')
      : '';

    // Desk context
    const showDeskContext = !!deskId && targetType !== 'desks';

    const deskContextText = showDeskContext
      ? desk
        ? t('activityLogs.in', { 0: t('common.desk') })
        : t('activityLogs.in_a_deleted_desk')
      : '';

    // Always pass these keys (empty fragment when hidden): omitting a Trans
    // component key makes i18next print the literal tag, e.g. <brainLink></brainLink> (MOV-16523).
    const brainLinkComponent = showBrainContext ? (
      <Link className={LINK_CLASSES} to={`/${slug}/ai-agents/${brainId}`} />
    ) : (
      <></>
    );

    const deskLinkComponent = showDeskContext ? (
      <Link className={LINK_CLASSES} to={`/${slug}/environments/${deskId}`} />
    ) : (
      <></>
    );

    // 4. Version created
    if (extra?.version) {
      return {
        i18nKey: 'activityLogs.sentence_version',
        values: {
          0: subject,
          1: translateVerb(verb, t),
          2: String(extra.version),
          3: brainContextText,
          4: showBrainContext ? (brain ?? '') : '',
        },
        components: { bold, muted, brainLink: brainLinkComponent },
      };
    }

    // Target component: link, plain text, or muted depending on state
    let targetComponent: React.JSX.Element;
    let targetDisplay: string;

    if (showTargetLink && targetLink) {
      targetComponent = <Link className={LINK_CLASSES} to={targetLink} />;
      targetDisplay = targetName ?? targetId;
    } else if (hideAgentLink && targetType === 'brains') {
      targetComponent = <span />;
      targetDisplay = targetName ?? targetId;
    } else {
      targetComponent = muted;
      targetDisplay = String(targetName ?? '');
    }

    // 5. Dialog (includes "of collection" context)
    if (targetType === 'dialogs') {
      return {
        i18nKey: 'activityLogs.sentence_dialog',
        values: {
          0: subject,
          1: translateVerb(verb, t),
          2: resourceType,
          3: targetDisplay,
          4: dialog?.collection || DEFAULT_COLLECTION,
          5: brainContextText,
          6: showBrainContext ? (brain ?? '') : '',
        },
        components: {
          bold,
          target: targetComponent,
          collection: muted,
          brainLink: brainLinkComponent,
        },
      };
    }

    // 6b. Grouped general (zero-th row with shared fields)
    if (isGrouped) {
      const toggleComponent = (
        <button type="button" onClick={handleToggle} className="cursor-pointer text-primary" />
      );

      if (!subject) {
        const capitalizedResourceType =
          resourceType.charAt(0).toUpperCase() + resourceType.slice(1);
        return {
          i18nKey: 'activityLogs.sentence_no_subject_with_count',
          values: {
            0: capitalizedResourceType,
            1: targetDisplay,
            2: brainContextText,
            3: showBrainContext ? (brain ?? '') : '',
            4: deskContextText,
            5: showDeskContext ? (desk ?? '') : '',
            6: translateVerb(verb, t),
            7: String(groupedItems),
          },
          components: {
            target: targetComponent,
            brainLink: brainLinkComponent,
            deskLink: deskLinkComponent,
            toggle: toggleComponent,
          },
        };
      }

      return {
        i18nKey: 'activityLogs.sentence_general_with_count',
        values: {
          0: subject,
          1: translateVerb(verb, t),
          2: resourceType,
          3: targetDisplay,
          4: brainContextText,
          5: showBrainContext ? (brain ?? '') : '',
          6: deskContextText,
          7: showDeskContext ? (desk ?? '') : '',
          8: String(groupedItems),
        },
        components: {
          bold,
          target: targetComponent,
          brainLink: brainLinkComponent,
          deskLink: deskLinkComponent,
          toggle: toggleComponent,
        },
      };
    }

    // 6. General (all other cases)
    return {
      i18nKey: 'activityLogs.sentence_general',
      values: {
        0: subject,
        1: translateVerb(verb, t),
        2: resourceType,
        3: targetDisplay,
        4: brainContextText,
        5: showBrainContext ? (brain ?? '') : '',
        6: deskContextText,
        7: showDeskContext ? (desk ?? '') : '',
      },
      components: {
        bold,
        target: targetComponent,
        brainLink: brainLinkComponent,
        deskLink: deskLinkComponent,
      },
    };
  };

  const sentence = getSentence();

  return (
    <div className="flex pt-1" data-testid="log">
      {/* Left gutter: optional indent + avatar + connector line */}
      {indentClass && <div className={indentClass} />}
      <div className="flex w-5 shrink-0 flex-col items-center">
        <div className="flex h-6 shrink-0 items-center">{avatar}</div>
        {showConnector && (
          <div className="mt-0.5 mb-0.5 min-h-3 w-px flex-1 bg-muted-foreground/40" />
        )}
      </div>

      {/* Right content: text row + optional field pills */}
      <div className="ml-1 max-w-3xl min-w-0 flex-1">
        <div className="flex min-h-6 items-center gap-2 text-sm leading-5 font-normal">
          <div className="min-w-0 flex-1 leading-normal">
            <Trans
              i18nKey={sentence.i18nKey}
              values={sentence.values}
              components={sentence.components}
            />
          </div>
          {!hideTimestamp && (
            <span className="shrink-0 text-muted-foreground">
              {Array.isArray(timestamp) ? (
                timeFromNow(timestamp[0]) === timeFromNow(timestamp[1]) ? (
                  <TimestampTooltip timestamp={timestamp[1]} side="left">
                    {timeFromNow(timestamp[1])}
                  </TimestampTooltip>
                ) : (
                  <>
                    <TimestampTooltip timestamp={timestamp[0]} side="left">
                      {timeFromNow(timestamp[0])}
                    </TimestampTooltip>
                    {' – '}
                    <TimestampTooltip timestamp={timestamp[1]} side="left">
                      {timeFromNow(timestamp[1])}
                    </TimestampTooltip>
                  </>
                )
              ) : timestamp ? (
                <TimestampTooltip timestamp={timestamp} side="left">
                  {timeFromNow(timestamp)}
                </TimestampTooltip>
              ) : (
                '-'
              )}
            </span>
          )}
        </div>

        <AuditLogFieldChanges extra={extra} className="mt-3 pb-3" getBrain={getBrain} />
        {children}
      </div>
    </div>
  );
};

export default TileActivityLog;
